import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useNavigate } from 'react-router-dom';
import { categories } from './CategoryFilter';

interface CreatePostProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreatePost({ isOpen, onClose }: CreatePostProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkBuckets = async () => {
      try {
        const { data: buckets, error } = await supabase
          .storage
          .listBuckets();

        if (error) {
          console.error('Erro ao listar buckets:', error);
          return;
        }

        console.log('Buckets disponíveis:', buckets);
      } catch (error) {
        console.error('Erro ao verificar buckets:', error);
      }
    };

    checkBuckets();
  }, []);

  const validateFile = (file: File) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return 'Formato de arquivo não suportado. Use JPG, PNG ou WebP.';
    }

    if (file.size > maxSize) {
      return 'A imagem deve ter no máximo 5MB';
    }

    return null;
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast({
        title: "Erro",
        description: error,
        variant: "destructive"
      });
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast({
        title: "Erro",
        description: error,
        variant: "destructive"
      });
      return;
    }

    setSelectedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!selectedImage || !user) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      console.log('Iniciando upload da imagem...');
      
      // Gerar nome único para o arquivo
      const timestamp = Date.now();
      const ext = selectedImage.name.split('.').pop();
      const fileName = `${user.id}/${timestamp}_${Math.random().toString(36).substring(2, 15)}.${ext}`;
      
      console.log('Caminho do arquivo:', fileName);
      
      // Upload da imagem
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('posts')
        .upload(fileName, selectedImage, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Erro no upload:', uploadError);
        throw uploadError;
      }

      console.log('Upload concluído:', uploadData);

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('posts')
        .getPublicUrl(fileName);

      console.log('URL pública:', publicUrl);

      // Criar post
      const { data: postData, error: postError } = await supabase
        .from('posts')
        .insert({
          title: title.trim(),
          caption: caption.trim(),
          image_url: publicUrl,
          user_id: user.id,
          category,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (postError) {
        console.error('Erro ao criar post:', postError);
        throw postError;
      }

      console.log('Post criado:', postData);

      toast({
        title: "Sucesso",
        description: "Post criado com sucesso!",
        variant: "default"
      });

      resetForm();
      onClose();
      
      // Redirect to user profile after successful post creation
      if (userProfile?.username) {
        navigate(`/perfil/${userProfile.username}`);
      }

    } catch (error: any) {
      console.error('Erro detalhado:', error);
      const errorMessage = error.message || 'Erro ao criar post. Tente novamente.';
      setError(errorMessage);
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setCaption('');
    setCategory('');
    setSelectedImage(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center px-4 py-3 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Nova Publicação</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Image Upload */}
              <div className="relative">
                {previewUrl ? (
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImage(null);
                        setPreviewUrl(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label 
                    className="block aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer transition-colors"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageSelect}
                      className="sr-only"
                    />
                    <div className="flex flex-col items-center justify-center h-full p-4 text-center">
                      <div className="w-12 h-12 mb-3 rounded-full bg-blue-50 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-blue-500" />
                      </div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Arraste sua imagem aqui
                      </p>
                      <p className="text-xs text-gray-500 mb-2">
                        ou clique para selecionar
                      </p>
                      <p className="text-xs text-gray-400">
                        JPG, PNG ou WebP até 5MB
                      </p>
                    </div>
                  </label>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título
                </label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Digite um título para sua publicação"
                  required
                  maxLength={100}
                />
              </div>

              {/* Caption */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Descreva seu projeto..."
                  maxLength={500}
                  rows={4}
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-colors"
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id === 'todos' ? '' : cat.id}>
                      {cat.label}
                    </option>
                  )).filter(cat => cat.props.value !== '')}
                </select>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-100">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isUploading || !selectedImage || !title.trim() || !category}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Publicando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Publicar
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}